/* eslint-disable i18next/no-literal-string */
import { maskitoNumberOptionsGenerator } from '@maskito/kit';
import { useMaskito } from '@maskito/react';
import { AmountUsd } from '@ui/components/Amount/AmountUsd';
import { ExclamationCircle } from '@ui/components/icons';
import { StatAmountUsd } from '@ui/components/Stats';
import { useTokenDisplayDecimals } from '@ui/hooks/useTokenDisplayDecimals';
import { formatNumber, formatWithThousandSeparators } from '@ui/lib/bignumber';
import { Portal } from '@ui/lib/park/portal';
import { Field } from '@ui/lib/park/ui/field';
import { Icon } from '@ui/lib/park/ui/icon';
import { Text } from '@ui/lib/park/ui/text';
import { OrderType } from '@ui/types/trade';
import { isNil } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef } from 'react';
import { Box, Flex, HStack, styled } from 'styled-system/jsx';

import { useMarketPrice } from '../../hooks/useMarketPrice';
import { useTradingPair } from '../../store/useTradingPair';
import { useTriggerPriceInputValidation } from './hooks/useTriggerPriceInputValidation';
import { useIsLong } from './store/useIsLong';
import { getTriggerPriceErrorMessage } from './utils/validateTriggerPrice';

type Props = Omit<Field.RootProps, 'onInput' | 'defaultValue'> & {
  value: string;
  orderType: OrderType;
  onInput: (value: string) => void;
};

export const TriggerPriceInput = ({
  onInput,
  value,
  orderType,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const errPortalRef = useRef<HTMLDivElement>(null);
  const isLong = useIsLong((s) => s._persistValue);
  const marketPrice = useMarketPrice();
  const selectedTradingPair = useTradingPair((s) => s.useTradingPair());

  const priceDisplayDecimals = useTokenDisplayDecimals(
    selectedTradingPair.baseSymbol,
    selectedTradingPair.priceDisplayDecimals,
  );
  const priceAllowanceSpace = 10 ** -priceDisplayDecimals;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const inputRefCallback = useMaskito({
    options: maskitoNumberOptionsGenerator({
      precision: priceDisplayDecimals,
      min: 0,
      thousandSeparator: ',',
    }),
  });

  useEffect(() => {
    if (orderType == OrderType.LIMIT && inputRef.current) {
      inputRef.current.focus();
    }
    if (orderType == OrderType.MARKET) {
      onInput('');
    }
  }, [orderType]);

  const triggerPriceValidation = useTriggerPriceInputValidation();

  const errorMessage = getTriggerPriceErrorMessage(
    t,
    triggerPriceValidation?.error,
  );

  return (
    <>
      <Field.Root
        bg="transparent"
        padding={orderType == OrderType.MARKET ? '2px 0' : '2px 0 0'}
        gap="8px"
        caretColor={isLong ? 'price.priceUp' : 'price.priceDown'}
        invalid={Boolean(errorMessage)}
        {...rest}
      >
        {orderType === OrderType.MARKET ? (
          <HStack justifyContent="space-between">
            <Field.Label
              textStyle="v2.sectionLabel"
              color="v2.text.subtle"
            >
              {t('common.Market Price (USD)')}
            </Field.Label>
            <Field.Label
              onClick={() => {
                if (isNil(marketPrice)) return;
                onInput(
                  (isLong
                    ? marketPrice - priceAllowanceSpace
                    : marketPrice + priceAllowanceSpace
                  ).toFixed(priceDisplayDecimals),
                );
              }}
              _hover={{
                cursor: 'pointer',
              }}
              textDecoration="underline"
            />
            <Flex alignItems="center" gap="0">
              <StatAmountUsd
                textStyle="numbers.small"
                color="v2.text.secondary"
                value={marketPrice}
                opts={{
                  displayDecimals: priceDisplayDecimals,
                }}
                data-cy-value={marketPrice}
              />
            </Flex>
          </HStack>
        ) : (
          <>
            <Flex justifyContent="space-between">
              <Field.Label
                textStyle="v2.sectionLabel"
                color="v2.text.subtle"
              >
                {t('common.Trigger Price (USD)')}
              </Field.Label>
              <Field.Label
                onClick={() => {
                  if (isNil(marketPrice)) return;
                  onInput(
                    (isLong
                      ? marketPrice - priceAllowanceSpace
                      : marketPrice + priceAllowanceSpace
                    ).toFixed(priceDisplayDecimals),
                  );
                }}
                _hover={{
                  cursor: 'pointer',
                }}
                textDecoration="underline"
              >
                <>
                  <Text as="span">{t('common.Market')}:</Text>{' '}
                  <AmountUsd
                    as="span"
                    value={marketPrice}
                    opts={{ prefix: '', displayDecimals: priceDisplayDecimals }}
                    data-cy="MarketPriceInTriggerPriceInput"
                    data-cy-value={marketPrice}
                  />
                </>
              </Field.Label>
            </Flex>
            <Flex
              bg="v2.bg.surface1"
              border="1px solid"
              borderColor="v2.border.default"
              borderRadius="4px"
              alignItems="center"
              gap="0"
              w="full"
              _focusWithin={{
                borderColor: 'v2.border.focus',
              }}
              transition="border-color 0.15s"
              p="4px 2px"
            >
              <Text
                display="flex"
                alignItems="center"
                color="v2.text.muted"
                pl="10px"
              >
                {isLong ? (
                  <styled.img
                    src="/icons/lessThan.svg"
                    alt="≤"
                    boxSize="10px"
                  />
                ) : (
                  <styled.img
                    src="/icons/ratherThan.svg"
                    alt=">="
                    boxSize="10px"
                  />
                )}
                $
              </Text>
              <Field.Input
                ref={(node) => {
                  inputRefCallback(node);
                  inputRef.current = node;
                }}
                onInput={(e) => {
                  const raw = e.currentTarget.value.replaceAll(',', '');
                  onInput(raw);
                }}
                value={formatWithThousandSeparators(value)}
                type="text"
                inputMode="decimal"
                textStyle="numbers.default"
                fontWeight="normal"
                cursor="text"
                padding="4px 10px"
                bg="transparent"
                border="none"
                outline="none"
                _hover={{
                  bg: 'transparent',
                }}
                _focus={{
                  bg: 'transparent',
                  boxShadow: 'none',
                  outline: 'none',
                }}
                placeholder={formatNumber(marketPrice ?? 0, {
                  // prefix: '$',
                  isShowSign: false,
                  displayDecimals: priceDisplayDecimals,
                })}
                w="full"
                data-cy="TriggerPriceInput"
              />
            </Flex>
          </>
        )}

        <Portal container={errPortalRef}>
          <Field.ErrorText>{errorMessage}</Field.ErrorText>
        </Portal>
      </Field.Root>
      <Box ref={errPortalRef} />
    </>
  );
};
